/**
 * Camlin Speech → Azure fallback for the browser mic path.
 *
 * Selecting `engine: 'camlin'` without a working token mint
 * (`GET /api/asr-token` → 503 when `CAMLIN_SPEECH_API_KEY` is missing)
 * used to leave the mic looking live while recognition never returned
 * a transcript. Probe the mint before capture; on failure stay on Azure
 * and keep any already-applied phrase list.
 */

import { configureSttEngine, getSttEngineConfig } from './sttEngine';

export function isCamlinTokenMintFailure(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return (
    /ASR token endpoint returned \d+/.test(message)
    || /ASR token endpoint returned no token/.test(message)
    || /Camlin ASR engine is not configured/.test(message)
  );
}

export function fallbackSttEngineToAzure(reason: string): void {
  const existing = getSttEngineConfig();
  if (existing.engine !== 'camlin') return;
  console.warn('[sttEngine] Camlin token mint failed — falling back to Azure', { reason });
  configureSttEngine({
    engine: 'azure',
    phrases: existing.phrases,
    includeContextTerms: existing.includeContextTerms,
    language: existing.language
  });
}

export async function resolveSttEngineForCapture(): Promise<'azure' | 'camlin'> {
  const cfg = getSttEngineConfig();
  if (cfg.engine !== 'camlin') return 'azure';
  if (!cfg.tokenUrl) {
    fallbackSttEngineToAzure('camlin engine missing tokenUrl');
    return 'azure';
  }
  try {
    const { probeCamlinAsrToken } = await import('./camlinAsr');
    await probeCamlinAsrToken(cfg.tokenUrl);
    return 'camlin';
  } catch (error) {
    fallbackSttEngineToAzure(
      error instanceof Error ? error.message : 'camlin token mint failed'
    );
    return 'azure';
  }
}
